import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import authProvider from "../../../authProvider";
import { LoadingIcon } from "../../../assets/icons/Icons";
import Logo from "../../../components/Logo";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import "../../../assets/scss/components/login.scss";
import { useThemeContext } from "../../../contexts/ThemeContext";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import { useUserContext } from "../../../contexts/UserContext";
import QRCode from "qrcode";
import OTPInput from "../../../components/OtpInput";
import CopyButton from "../../../components/CopyButton";
import { getSubdomain } from "../../../utils/whitelabel";

function Index(props) {
  const mfaData = JSON.parse(sessionStorage.getItem("mfa_user_sub") || "{}");
  const { t, i18n } = useTranslation();
  const { theme } = useThemeContext();
  const { setIsAuth } = useGlobalContext();
  const { setUserData } = useUserContext();
  const [loginError, setLoginError] = useState("");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [otpCode, setOtpCode] = useState(["", "", "", "", "", ""]);

  useEffect(() => {
    if (mfaData && !mfaData.mfa_required) {
      // If MFA is not required, redirect to home
      navigate("/");
    } else if (!mfaData || !mfaData.mfa_required) {
      // If no MFA data found, redirect to login
      navigate("/user/login");
    } else if (mfaData && mfaData.mfa_registered) {
      // If MFA is already registered, redirect to MFA verify page
      navigate("/auth/mfa-verify");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaData]);

  // Default labels that can be overridden
  const defaultLabels = {
    qrInstruction: i18n.t("qrInstruction"),
    copySecretTooltip: i18n.t("copySecretTooltip"),
    authenticatorApps: i18n.t("authenticatorApps"),
    otpLabel: i18n.t("otpLabel"),
  };

  // Generate dynamic branding info
  const getBrandingInfo = () => {
    const { hostname } = window.location;
    const subdomain = getSubdomain(hostname);

    // Use full subdomain for branding, replace dots with spaces
    // For hrblockadvantagecard.staging.payaccount.io -> "hrblockadvantagecard staging Payaccount"
    let companyName;
    if (subdomain && subdomain.toUpperCase() !== "UNBRANDED") {
      const formattedSubdomain = subdomain.replace(/\./g, " ");
      companyName = `${formattedSubdomain} Payaccount`;
    } else {
      companyName = "Berkeley Payaccount";
    }

    return {
      issuer: companyName,
      username: (mfaData && mfaData.user_name) || "Cardholder", // Use username from backend (actual username for cardholder portal)
      domain: hostname,
    };
  };

  const { issuer, username } = getBrandingInfo();

  const generateTOTPUri = (secretCode, usernameProp, issuerProp) => {
    return `otpauth://totp/${encodeURIComponent(
      issuerProp
    )}:${encodeURIComponent(
      usernameProp
    )}?secret=${secretCode}&issuer=${encodeURIComponent(issuerProp)}`;
  };

  const generateQRCode = async (secretCode) => {
    try {
      const totpUri = generateTOTPUri(secretCode, username, issuer);
      const qrDataUrl = await QRCode.toDataURL(totpUri, {
        width: 200,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });
      setQrCodeUrl(qrDataUrl);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to generate QR code:", err);
    }
  };

  useEffect(() => {
    if (mfaData && mfaData.secret_code) {
      generateQRCode(mfaData.secret_code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaData, issuer, username]);

  const handleOTPComplete = (code) => {
    if (!mfaData || !mfaData.session || !mfaData.user_sub) {
      return;
    }
    // onSetupComplete(mfaData.user_sub, mfaData.session, code);
  };

  const handleOTPChange = (newCode) => {
    setOtpCode(newCode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    authProvider
      .mfaSetup({
        user_sub: mfaData.user_sub,
        session: mfaData.session,
        otp: otpCode.join(""),
      })
      .then(({ userData }) => {
        authProvider
          .checkAuth()
          .then(() => {
            // Token is now in httpOnly cookie
            setIsAuth(true);
            setUserData(userData);
            setLoading(false);
            setLoginError("");
            navigate("/");
          })
          .catch(() => {
            setUserData(null);
            setLoading(false);
            setLoginError("Login failed, Authentication invalid!");
          });
      })
      .catch((error) => {
        setLoading(false);
        const errorMessage = error.message || "Login failed";
        const errorCode = error.response?.error?.code;
        setLoginError(errorCode ? `errors_${errorCode}` : errorMessage);
      });
  };
  const handleSkipMFASetup = () => {
    // Clear MFA session data
    sessionStorage.removeItem("mfa_user_sub");
    // Redirect to dashboard, allowing user to skip MFA setup for now
    navigate("/");
  };
  const handleLogout = () => {
    // Clear MFA session data
    sessionStorage.removeItem("mfa_user_sub");
    authProvider.logout();
    navigate("/user/login");
  };
  return (
    <div>
      <div className="mt-3 text-center">
        <Logo
          className="logo"
          src={theme?.logo}
          style={{ maxWidth: "200px", objectFit: "contain" }}
        />
      </div>
      <div className="login">
        <div className="pl-3 pr-3">
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <LanguageSwitcher />
          </div>
          <div className="mt-3 text-center">
            <h3 className="mb-4">
              {t("Set Up MFA for")} {t(`${theme?.copyright_company_name}`)}
            </h3>
          </div>
          <form
            action=""
            method="post"
            className="mb-2"
            onSubmit={handleSubmit}
          >
            <div className="row mb-2">
              <div className="col-xs-12">
                <div>
                  <label style={{ fontSize: "14px", marginBottom: "15px" }}>
                    {defaultLabels.qrInstruction}
                  </label>

                  {/* QR Code and Copy Button side by side */}
                  {qrCodeUrl && (
                    <div
                      style={{
                        marginBottom: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "15px",
                        flexWrap: "wrap",
                      }}
                    >
                      <img
                        src={qrCodeUrl}
                        alt="MFA QR Code"
                        style={{
                          border: "1px solid #ddd",
                          borderRadius: "5px",
                          padding: "10px",
                          backgroundColor: "white",
                        }}
                      />
                      {/* Copy button positioned next to QR code */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <CopyButton
                          value={mfaData.secret_code}
                          title={defaultLabels.copySecretTooltip}
                        />
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#666",
                            textAlign: "center",
                          }}
                        >
                          {i18n.t("Copy Secret")}
                        </span>
                      </div>
                    </div>
                  )}
                  <p style={{ fontSize: "12px", color: "#666" }}>
                    {defaultLabels.authenticatorApps}
                  </p>
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-xs-12">
                <label htmlFor="otp">{t(defaultLabels.otpLabel)}</label>
                <div className="input-wrapper">
                  {/* OTP Input Component */}
                  <OTPInput
                    value={otpCode}
                    onChange={handleOTPChange}
                    onComplete={handleOTPComplete}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 mb-3">
              <button
                className={`btn btn-primary ${loading ? "btn-loading" : ""}`}
                style={{ width: "100%" }}
                type="submit"
                name="submit"
                disabled={loading || otpCode.includes("")}
              >
                {loading && <LoadingIcon />}
                {i18n.t("Continue")}
              </button>
              <button
                className="btn btn-secondary"
                style={{ width: "100%" }}
                type="button"
                name="cancel"
                onClick={handleSkipMFASetup}
              >
                {i18n.t("Skip for now")}
              </button>
            </div>
            <button
              className="btn btn-link btn-medium"
              style={{ width: "100%", color: "#212121" }}
              type="button"
              name="cancel"
              onClick={handleLogout}
            >
              {i18n.t("Back to Login")}
            </button>
            {loginError && (
              <div className="text-center">
                <span className="form-error">{i18n.t(loginError)}</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default Index;
